import { Controller } from "@hotwired/stimulus";

type TagFilterMethod = "equals" | "not_equals" | "starts_with";

// Connects with data-controller="test-engine-filters"
export default class extends Controller<HTMLElement> {
  static targets = ["tagDialog", "tagDialogKey", "tagDialogValue", "tagDialogFilterMethod"];
  declare tagDialogTarget: HTMLDialogElement;
  declare tagDialogKeyTarget: HTMLInputElement;
  declare tagDialogValueTarget: HTMLInputElement;
  declare tagDialogFilterMethodTarget: HTMLSelectElement;

  // If turboFrameId is set, it will use Turbo.visit to visit the new URL within the turbo frame.
  // Otherwise, it will visit the new URL at the page level.
  declare turboFrameId?: string;
  declare turboFrame: Element | null;
  declare url: URL;

  connect() {
    this.turboFrameId = this.element.dataset.turboFrameId;
    this.turboFrame = this.element.closest(`turbo-frame#${this.turboFrameId}`);
    if (this.turboFrame) {
      this.url = new URL(this.turboFrame.getAttribute("src") as string);
    } else {
      this.url = new URL(window.location.href);
    }
  }

  // Set filter in the URL params and visit the new URL.
  // This action is triggered when the filter option is clicked, except for tags.
  setFilter(event: Event) {
    event.preventDefault();

    const target = event.currentTarget as HTMLElement;
    const name = target.getAttribute("data-name") as string;
    const value = target.getAttribute("data-value") as string;

    const params = new URLSearchParams(this.url.search);

    switch (name) {
      case "labels":
      case "owners":
        this.setArrayParam(params, name, value);
        break;
      case "result":
        this.setTagParam(params, "result", value, "equals");
        break;
      default:
        if (value === "") {
          params.delete(name);
        } else {
          params.set(name, value);
        }
        break;
    }

    this.resetPagination(params);

    this.url.search = params.toString();
    Turbo.visit(this.url.toString(), { frame: this.turboFrameId });
  }

  // Remove filter from the URL params and visit the new URL.
  // This action is triggered when the remove button is clicked.
  removeFilter(event: Event) {
    event.preventDefault();

    const target = event.currentTarget as HTMLElement;
    const name = target.getAttribute("data-name") as string;
    const value = target.getAttribute("data-value") as string;

    const params = new URLSearchParams(this.url.search);

    switch (name) {
      // For "state", we simply remove the key from the param.
      case "state":
        params.delete(name);
        break;
      // For branch, we remove branch key from the params, as well as `scm.branch` tags param
      // in case it exists (i.e. added by test execution tag filter)
      case "branch":
        params.delete(name);
        this.setTagParam(params, "scm.branch", "", "equals");
        break;
      // For "labels" and "owners", we remove the value from the array param.
      case "labels":
      case "owners":
        this.removeArrayParamValue(params, name, value);
        break;
      // For "results" and "tags", we remove the key from the tag param by setting the key value to empty string.
      default:
        this.setTagParam(params, name, "", "equals");
        break;
    }

    this.resetPagination(params);

    this.url.search = params.toString();
    Turbo.visit(this.url.toString(), { frame: this.turboFrameId });
  }

  // Set tag filter in the URL params and visit the new URL.
  // This action is triggered when the tag dialog form is submitted.
  setTagFilter(event: Event) {
    event.preventDefault();

    const form = event.currentTarget as HTMLFormElement;
    const filterMethod = form.filter_method.value;
    const tagValue = form.tag_value.value;
    const tagKey = form.tag_key.value;

    const params = new URLSearchParams(this.url.search);

    // We use tag dialog for setting custom branch, if the form is submitted for `scm.branch` tag key,
    // we set the branch param instead of tags param.
    if (tagKey === "scm.branch") {
      params.set("branch", this.valueForFilterMethod(filterMethod, tagValue));
    } else {
      this.setTagParam(params, tagKey, tagValue, filterMethod);
    }

    this.resetPagination(params);

    this.url.search = params.toString();
    Turbo.visit(this.url.toString(), { frame: this.turboFrameId });
  }

  // Open the tag dialog for a specific tag and set the key and value for the form in the dialog.
  // This action is trigger when the tag option is clicked.
  openTagDialog(event: Event) {
    const target = event.currentTarget as HTMLElement;
    const key = target.getAttribute("data-tag-key") as string;
    const value = target.getAttribute("data-value") as string;
    const filterMethod = target.getAttribute("data-filter-method") as string;

    // set filter method options based on tag key
    this.populateFilterMethodOptions(key, filterMethod);

    // Set the tag key and value for the form in the dialog
    this.tagDialogKeyTarget.value = key;
    this.tagDialogFilterMethodTarget.value = filterMethod;
    this.tagDialogValueTarget.value = value;

    this.tagDialogTarget.showModal();
  }

  // Hide options that do not match the search input.
  // If there are no matching options, show the empty state.
  searchFilterOptions(event: Event) {
    const target = event.currentTarget as HTMLInputElement;
    const search = target.value.toLowerCase();
    const groupName = target.name;

    const options = document
      .querySelector(`[data-filter-group="${groupName}"]`)
      ?.querySelectorAll("li");

    let filteredOptionsCount = 0;

    options?.forEach((option) => {
      if (option.textContent?.toLowerCase().includes(search)) {
        option.classList.remove("hidden");
        filteredOptionsCount++;
      } else {
        option.classList.add("hidden");
      }
    });

    // Show/hide empty state
    const emptyState = document
      .querySelector(`[data-filter-group="${groupName}"]`)
      ?.querySelector("[data-empty-state]");
    if (filteredOptionsCount === 0) {
      emptyState?.classList.remove("hidden");
    } else {
      emptyState?.classList.add("hidden");
    }
  }

  // Change filter method in existing filter component and reload page
  // This action is triggered when the filter method dropdown in a filter button clicked
  changeFilterMethod(event: Event) {
    const target = event.currentTarget as HTMLButtonElement;
    const filterKey = target.getAttribute("data-filter-key") as string;
    const filterValue = target.getAttribute("data-filter-value") as string;
    const filterMethod = target.getAttribute("data-filter-method") as TagFilterMethod;

    if (!filterKey || !filterValue || !filterMethod) {
      return;
    }

    // We don't currently support changing filter method except for tags.
    // If the filter key is not a tag, we don't do anything.
    if (["branch", "labels", "owners", "result", "state"].includes(filterKey)) {
      return;
    }

    const params = new URLSearchParams(this.url.search);

    this.setTagParam(params, filterKey, filterValue, filterMethod);
    this.url.search = params.toString();
    Turbo.visit(this.url.toString(), { frame: this.turboFrameId });
  }

  closeTagDialog() {
    this.tagDialogTarget.close();

    // Reset the tag key and value for the dialog
    this.tagDialogKeyTarget.value = "";
    this.tagDialogFilterMethodTarget.value = "";
    this.tagDialogValueTarget.value = "";
  }

  private populateFilterMethodOptions(key: string, selectedFilterMethod: string) {
    // Define filter options based on tag key
    const filterOptions =
      key === "scm.branch"
        ? [
            { label: "is", value: "equals" },
            { label: "starts with", value: "starts_with" },
          ]
        : [
            { label: "is", value: "equals" },
            { label: "is not", value: "not_equals" },
            { label: "starts with", value: "starts_with" },
          ];

    const select = this.tagDialogFilterMethodTarget;
    const existingOptions = Array.from(select.options);

    // Check if we need to update the options
    const needsUpdate =
      existingOptions.length !== filterOptions.length ||
      existingOptions.some(
        (option, index) => !filterOptions[index] || option.value !== filterOptions[index].value,
      );

    if (needsUpdate) {
      // Only clear and rebuild if the structure has changed
      select.innerHTML = "";
      filterOptions.forEach(({ label, value }, index) => {
        const isDefault = index === 0;
        select.appendChild(new Option(label, value, isDefault, false));
      });
    }

    // Always update the selected value
    select.value = selectedFilterMethod;
  }

  private removeArrayParamValue(params: URLSearchParams, name: string, value: string) {
    const filterParam = params.get(name) || "";

    const values = filterParam.split(",").filter((val) => val !== value);

    if (values.length === 0) {
      params.delete(name);
      return;
    }
    params.set(name, values.join(","));
  }

  private setArrayParam(params: URLSearchParams, name: string, value: string) {
    const filterParam = params.get(name) || "";
    const filters = new Set<string>();

    filterParam
      .split(",")
      .filter(Boolean)
      .forEach((filter) => {
        filters.add(filter);
      });

    // Add value to the array if it doesn't exist, or remove it if it does
    if (filters.has(value)) {
      filters.delete(value);
    } else {
      filters.add(value);
    }

    if (filters.size === 0) {
      params.delete(name);
    } else {
      params.set(name, [...filters].join(","));
    }
  }

  private setTagParam(
    params: URLSearchParams,
    key: string,
    value: string,
    filterMethod: TagFilterMethod,
  ) {
    const tagsParam = params.get("tags") || "";
    const tags = new Map<string, string>();

    const tagValue = this.valueForFilterMethod(filterMethod, value);

    tagsParam
      .split(",")
      .filter(Boolean)
      .forEach((tag) => {
        const [key, value] = tag.split(":");
        tags.set(key, value);
      });

    if (tagValue) {
      tags.set(key, tagValue);
    } else {
      tags.delete(key);
    }

    if (tags.size === 0) {
      params.delete("tags");
    } else {
      params.set("tags", [...tags].map(([key, value]) => `${key}:${value}`).join(","));
    }
  }

  private valueForFilterMethod(filterMethod: TagFilterMethod, value: string) {
    switch (filterMethod) {
      case "equals":
        return value;
      case "not_equals":
        return `!${value}`;
      case "starts_with":
        return `${value}*`;
    }
  }

  // When updating the filters, we reset the page to the first one to avoid the
  // user landing on a page that has no results.
  private resetPagination(params) {
    params.set("page", 1);
  }
}
